<template>
  <div class="col-12">
    <div class="row mb-4">
      <div class="col-12 col-md-4">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-drop-down
            id="alternativeCategoryDd"
            v-model="model.categoryAlternativePlacement"
            label="Categoria de Alternativas"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
            :error="errors[0]"
            :disabled="readonly"
            custom-error-msg="Favor de seleccionar la categoría alternativa de ubicación"
            :options="categoryAlternatives"
            @input="getAlternativesPlacement"
          />
        </validation-provider>
      </div>
       <div class="col-12 col-md-4" v-if="model.categoryAlternativePlacement">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-drop-down
            id="alternativePlacementDd"
            v-model="model.alternativePlacement"
            label="Nombre de Alternativa"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
            :error="errors[0]"
            :disabled="alternativePlacements.length == 1 || readonly"
            custom-error-msg="Favor de seleccionar el nombre de la alternativa de ubicación."
            :options="alternativePlacements"
          />
        </validation-provider>
      </div>
    </div>
    <div class="row mb-4">
      <div class="col-12 col-md-4">
        <validation-provider v-slot="{ errors }" rules="required">
          <custom-drop-down
            id="alternativeCategoryDd"
            v-model="model.primaryLanguage"
            label="Idioma primario del estudiante"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
            :error="errors[0]"
            :disabled="readonly"
            custom-error-msg="Favor de seleccionar el idioma primario del estudiante."
            :options="primaryLanguages"
          />
        </validation-provider>
      </div>
    </div>
    <div class="row mb-4" v-if="otherPrimaryLanguageSelected">
       <div class="col-12 col-md-4">
        <validation-provider v-slot="{ errors }" rules="required">
            <custom-input
              id="otherPrimaryLanguage"
              v-model="model.otherPrimaryLanguage"
              :error="errors[0]"
              :custom-error-msg="'Campo es requerido.'"
              type="text"
              maxlength="20"
              :disabled="readonly"
            />
        </validation-provider>
      </div>
    </div>
    <template>
      <SectionTitle has-line title="Documentos" class="mt-3" />
      <DocumentsForm
        ref="documentsFormRef"
        class="mt-3"
        :show-title="false"
        :documents="value.documents"
        :allowed-document-types="documentTypes"
        :multiple-uploads-document-type-ids="[1194]"
        :required-document-type-ids="requiredDocumentTypeIds"
        :read-only="readonly"
        :minute-document-type-id="minuteDocumentTypeId"
        :is-compu-minute-system-selection-enabled="isCompuMinuteSystemSelectionEnabled"
        :student-sie-id="studentSieId"
        :minute-info="minuteInfo"
        :on-go-to-draft-minute="onGoToDraftMinute"
        @minute-info-input="$emit('minute-info-input', $event)"
      />
      <ErrorComponent
        v-for="error in documentErrors"
        :message="error"
        variant="no-border error-margin-left mb-2"
      />
    </template>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import {
  getEFADocumentTypes,
  getPrimaryStudentLanguages
} from '@/modules/common/code.api.js';
import { getCategories, getNames } from '@/views/pei/location-alternatives/_api';
import DocumentsForm from '@/views/humanResources/request/components/DocumentsForm';
import CompuMinuteSelection from '@/components/shared/CompuMinuteSelection.vue';
import ErrorComponent from '@/components/common/Error';
import documentTypes from '@/utils/constants/documentTypes';
import alternatePlacementsTypes from '@/utils/constants/alternatePlacements';
import categoryAlternativePlacement from '@/utils/constants/categoryAlternativePlacement';

const EXCLUDE_ALTERNATIVES = []; //Itinerante;
const OTHER_LANGUAGE = 4; //Otro;

export default {
  name: 'StudentAlternatePlacement',
  components: {
    SectionTitle,
    CustomDropDown,
    DocumentsForm,
    CompuMinuteSelection,
    ErrorComponent,
    CustomInput
  },
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    readonly: {
      type: Boolean,
      default: () => false,
    },
    documentErrors: {
      type: Array,
      default: () => [],
    },
    isCompuMinuteSystemSelectionEnabled: {
      type: Boolean,
      default: false,
    },
    studentSieId: {
      type: [Number, String],
      default: null,
    },
    minuteInfo: {
      type: Object,
      default: () => ({
        useSystemMinute: null,
        compuMinuteId: null,
        fileId: null,
        fileName: null,
      }),
    },
    onGoToDraftMinute: {
      type: Function,
      default: null,
    },
  },
  data: () => ({
    categoryAlternatives: [],
    alternativePlacements: [],
    primaryLanguages: [],
    documentTypes: [],
    disabled: false,
    otherPrimaryLanguageSelected: false,
    model: {
      categoryAlternativePlacement: null,
      alternativePlacement: null,
      documents: [],
      primaryLanguage: null,
      otherPrimaryLanguage: null,
    },
  }),
  async created() {
    await this.getCategoryAlternatives();
    await this.getDocumentTypes();
    await this.getPrimaryStudentLanguages();
  },
  computed: {
    studentAge() {
      return this.$store.getters['$_student/getMipeStudent'].age;
    },
    minuteDocumentTypeId() {
      return documentTypes.SAEE_Minute;
    },
    hasMinuteAttached() {
      return (
        this.value.documents.some(
          (document) => document.documentTypeId === documentTypes.SAEE_Minute
        ) || this.minuteInfo.useSystemMinute === true
      );
    },
    requiredDocumentTypeIds() {
      const categoryId = this.model.categoryAlternativePlacement?.id;
      const alternativePlacementId = this.model.alternativePlacement?.id;
      const requiredIds = [];
      const requiresEfa04 = [
        categoryAlternativePlacement.SalonRecurso,
        categoryAlternativePlacement.SalonEspecialSEM,
        categoryAlternativePlacement.Hogar,
        categoryAlternativePlacement.Itinerante
      ].includes(categoryId);
      const requiresEfa03 = [
        alternatePlacementsTypes.SalonEspecialEnEscuelaRegular,
        alternatePlacementsTypes.SalonRegularConServiciosDeSalonRecurso,
        alternatePlacementsTypes.SalonRegularConServiciosRelacionados,
        alternatePlacementsTypes.SalonRegularConServiciosYAyudaSuplementaria,
        alternatePlacementsTypes.SalonRegularConServiciosSuplementarios
      ].includes(alternativePlacementId);

      if (categoryId) {
        requiredIds.push(
          requiresEfa04
            ? documentTypes.SAEE_EFA4
            : documentTypes.SAEE_EFA3
        );
      }

      if (requiresEfa03 && !requiredIds.includes(documentTypes.SAEE_EFA3)) {
        requiredIds.push(documentTypes.SAEE_EFA3);
      }

      return requiredIds;
    },
  },
  methods: {
    async validateMinute() {
      if (!this.isCompuMinuteSystemSelectionEnabled) return true;

      return this.hasMinuteAttached;
    },
    async getCategoryAlternatives() {
      const data = await getCategories();
      this.categoryAlternatives = [...data.filter(x=> !EXCLUDE_ALTERNATIVES.includes(x.id))];
    },
    async getAlternativesPlacement() {
      this.model.alternativePlacement = null;
      if(this.model.categoryAlternativePlacement)
      {
        const data = await getNames({
          categoryId: this.model.categoryAlternativePlacement.id
        });
        this.alternativePlacements = [...data];
        if(this.alternativePlacements.length == 1)
        {
          this.model.alternativePlacement = this.alternativePlacements[0];
        }
      }
    },
    async getDocumentTypes() {
      const { data } = await getEFADocumentTypes();
      this.documentTypes = [...data];
    },
    async getPrimaryStudentLanguages(){
      const { data } = await getPrimaryStudentLanguages();
      this.primaryLanguages = data;
    }
  },
  watch: {
    value: {
      deep: true,
      handler(newValue) {
        this.model = newValue;
      }
    },
    model: {
      deep: true,
      handler(newValue) {
        this.$emit('input', newValue);
      }
    },
    'model.primaryLanguage':{
      deep: true,
      handler(newValue) {
        this.otherPrimaryLanguageSelected = false;
        this.model.otherPrimaryLanguage = null;
        if(newValue?.id == OTHER_LANGUAGE){
          this.otherPrimaryLanguageSelected = true;
        }
      },
    }
  }
};
</script>
